import { useState } from 'react';
import { Link } from 'react-router-dom';
import ImageCarousel from '../../../components/feature/ImageCarousel';

interface ProductModel {
  name: string;
  dimensions: string;
  area: string;
  occupancy: string;
  image: string;
  features: string[];
  keyFeature: string;
  price: string;
  galleryImages: { url: string; label: string }[];
  materialList: {
    mainFrame: string;
    exteriorWall: string[];
    glass: string[];
    wall: string[];
    floor: string;
    insulation: string;
  };
  sellingPoints: string;
}

export default function ProductGrid() {
  const [selectedProduct, setSelectedProduct] = useState<ProductModel | null>(null);

  const models: ProductModel[] = [
    {
      name: 'X3',
      dimensions: '5.6m × 3.3m × 3.2m',
      area: '18 m²',
      occupancy: '2 persons',
      price: 'FROM $45,760',
      image: '/images/71de8de379a3.webp',
      features: ['Compact Design', 'High-end Glamping', 'Private Studios'],
      keyFeature: 'Compact and agile, perfect for high-end glamping or private studios.',
      galleryImages: [
        { url: '/images/ai-luxury-compact-capsule-house-interior-mo-e185f1.webp', label: 'Interior View' },
        { url: '/images/09d9325f4698.webp', label: 'Floor Plan' },
        { url: '/images/71de8de379a3.webp', label: 'Exterior View' }
      ],
      materialList: {
        mainFrame: 'Galvanized steel frame structure 5.0',
        exteriorWall: ['Aluminium alloy aluminium veneer 2.0', 'Metal fluorocarbon paint baking paint'],
        glass: ['6+15A+6 hollow Low E tempered glass'],
        wall: ['Advanced environmentally friendly carbon crystal panel'],
        floor: '6cm SPC premium stone crystal locking floor',
        insulation: '100mm polyurethane insulation layer'
      },
      sellingPoints: 'The X3 is our most compact and agile model, perfect for high-end glamping destinations or private creative studios. Despite its compact footprint, it delivers the full X Series experience with premium materials, smart technology integration, and exceptional build quality. Ideal for those seeking luxury in a smaller package.'
    },
    {
      name: 'X5',
      dimensions: '8.5m × 3.3m × 3.2m',
      area: '28 m²',
      occupancy: '2-4 persons',
      price: 'FROM $54,670',
      image: '/images/65c0b0130fa8.webp',
      features: ['Panoramic Balcony', 'Bedroom Skylight', 'Nature Experience'],
      keyFeature: 'Features a panoramic balcony and bedroom skylight for an immersive nature experience.',
      galleryImages: [
        { url: '/images/ai-spacious-capsule-house-interior-panorami-aac791.webp', label: 'Interior View' },
        { url: '/images/cc88e092394d.webp', label: 'Floor Plan' },
        { url: '/images/65c0b0130fa8.webp', label: 'Exterior View' }
      ],
      materialList: {
        mainFrame: 'Galvanized steel frame structure 5.0',
        exteriorWall: ['Aluminium alloy aluminium veneer 2.0', 'Metal fluorocarbon paint baking paint'],
        glass: ['6+15A+6 hollow Low E tempered glass'],
        wall: ['Advanced environmentally friendly carbon crystal panel'],
        floor: '6cm SPC premium stone crystal locking floor',
        insulation: '100mm polyurethane insulation layer'
      },
      sellingPoints: 'The X5 brings the outdoors in with its signature panoramic balcony and bedroom skylight. This model is designed for those who want to feel connected to nature while enjoying all modern comforts. The expanded living space accommodates couples or small families perfectly, making it ideal for eco-tourism resorts or permanent residences.'
    },
    {
      name: 'X6',
      dimensions: '9.5m × 3.3m × 3.2m',
      area: '31 m²',
      occupancy: '2-4 persons',
      price: 'FROM $62,150',
      image: '/images/8ccdb45466f9.webp',
      features: ['Extended Space', 'Equipment Room', 'Maximum Utility'],
      keyFeature: 'Extended living space with a dedicated equipment room for maximum utility.',
      galleryImages: [
        { url: '/images/ai-large-capsule-house-interior-extended-li-6d8a69.webp', label: 'Interior View' },
        { url: '/images/992aab6460ee.webp', label: 'Floor Plan' },
        { url: '/images/8ccdb45466f9.webp', label: 'Exterior View' }
      ],
      materialList: {
        mainFrame: 'Galvanized steel frame structure 5.0',
        exteriorWall: ['Aluminium alloy aluminium veneer 2.0', 'Metal fluorocarbon paint baking paint'],
        glass: ['6+15A+6 hollow Low E tempered glass'],
        wall: ['Advanced environmentally friendly carbon crystal panel'],
        floor: '6cm SPC premium stone crystal locking floor',
        insulation: '100mm polyurethane insulation layer'
      },
      sellingPoints: 'The X6 offers extended living space with a dedicated equipment room, making it perfect for off-grid installations or locations requiring additional utility space. The extra room can house water treatment systems, battery storage, or serve as additional storage. This model maximises functionality without compromising on luxury.'
    },
    {
      name: 'X7',
      dimensions: '11.5m × 3.3m × 3.2m',
      area: '38 m²',
      occupancy: '2-4 persons',
      price: 'FROM $66,660',
      image: '/images/8d92522722bb.webp',
      features: ['Maximum Luxury', 'Panoramic Views', 'Spacious Layout'],
      keyFeature: 'The flagship model offering maximum luxury, large panoramic views, and a spacious layout.',
      galleryImages: [
        { url: '/images/ai-flagship-luxury-capsule-house-interior-m-810688.webp', label: 'Interior View' },
        { url: '/images/53d24f4a9591.webp', label: 'Floor Plan' },
        { url: '/images/8d92522722bb.webp', label: 'Exterior View' }
      ],
      materialList: {
        mainFrame: 'Galvanized steel frame structure 5.0',
        exteriorWall: ['Aluminium alloy aluminium veneer 2.0', 'Metal fluorocarbon paint baking paint'],
        glass: ['6+15A+6 hollow Low E tempered glass'],
        wall: ['Advanced environmentally friendly carbon crystal panel'],
        floor: '6cm SPC premium stone crystal locking floor',
        insulation: '100mm polyurethane insulation layer'
      },
      sellingPoints: 'The X7 is our flagship model, representing the pinnacle of the X Series. With maximum living space, expansive panoramic views, and a spacious layout, it delivers an uncompromising luxury experience. Perfect for permanent residences, premium vacation rentals, or executive retreats where only the best will do.'
    },
    {
      name: 'Y6',
      dimensions: '7.5m × 3.3m × 3.2m',
      area: '24.7 m²',
      occupancy: '2 persons',
      price: 'FROM $53,130',
      image: '/images/nx-y6-white-background-46281b.webp',
      features: ['Cabin Style', 'Wooden Exterior', 'Balcony Deck', 'Nature Connected'],
      keyFeature: 'Cabin-style design with wooden exterior and panoramic balcony, offering a warm and immersive nature experience.',
      galleryImages: [
        { url: '/images/nx-y6-white-background-46281b.webp', label: 'Exterior View' },
        { url: '/images/nx-y6-white-background-46281b.webp', label: 'Product View' },
        { url: '/images/32b9612143bf.webp', label: 'Floor Plan' },
      ],
      materialList: {
        mainFrame: 'Galvanized steel frame structure 5.0',
        exteriorWall: ['Aluminium alloy aluminium veneer 2.0', 'Metal fluorocarbon paint baking paint'],
        glass: ['6+15A+6 hollow Low E tempered glass'],
        wall: ['Advanced environmentally friendly carbon crystal panel'],
        floor: '6cm SPC premium stone crystal locking floor',
        insulation: '100mm polyurethane insulation layer'
      },
      sellingPoints: 'The Y6 brings a unique cabin aesthetic to the X Series with its wooden exterior and panoramic balcony. Designed for those who want a cozy, nature-connected living experience with modern comforts and the same premium build quality you expect from Nexa Pods.'
    },
    {
      name: 'V6',
      dimensions: 'GF: 5.55m × 4.05m × 2.55m / SF: 5.55m × 2.92m × 2.55m',
      area: '22.4 m² + 16 m²',
      occupancy: '2-4 persons',
      price: 'FROM $72,160',
      image: '/images/nx-p03-img01-4472ca.webp',
      features: ['Triangle Design', 'Double Layer', 'Glass Facade', 'Maximum Space'],
      keyFeature: 'Innovative triangle-shaped double-layer design with a glass facade, maximising vertical space efficiency and panoramic views.',
      galleryImages: [
        { url: '/images/nx-p03-img01-4472ca.webp', label: 'Exterior View' },
        { url: '/images/e698e533a57a.webp', label: 'Floor Plan' },
        { url: '/images/nx-v6-exterior-white-197bf4.webp', label: 'Product View' },
        { url: '/images/nx-v6-resort-night-ce2747.webp', label: 'Resort Night View' },
        { url: '/images/nx-v6-resort-pool-ecacf3.webp', label: 'Resort Pool Application' },
        { url: '/images/nx-v6-resort-concept-b3195a.webp', label: 'Resort Concept' }
      ],
      materialList: {
        mainFrame: 'Galvanized steel frame structure 5.0',
        exteriorWall: ['Aluminium alloy aluminium veneer 2.0', 'Metal fluorocarbon paint baking paint'],
        glass: ['6+15A+6 hollow Low E tempered glass'],
        wall: ['Advanced environmentally friendly carbon crystal panel'],
        floor: '6cm SPC premium stone crystal locking floor',
        insulation: '100mm polyurethane insulation layer'
      },
      sellingPoints: 'The V6 Triangle House introduces a bold architectural statement to the X Series. Its innovative double-layer design maximises vertical space efficiency while the dramatic glass facade creates an immersive connection with the surrounding environment. Perfect for those seeking a distinctive and spacious living experience.'
    }
  ];

  const openModal = (product: ProductModel) => {
    setSelectedProduct(product);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedProduct(null);
    document.body.style.overflow = 'unset';
  };

  const getCarouselImages = (product: ProductModel) => {
    return product.galleryImages;
  };

  return (
    <section id="models" className="py-20 sm:py-28 lg:py-36 bg-gradient-to-b from-[#0A0A0A] via-[#0D1117] to-[#000000]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 sm:mb-20">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-thin mb-6 text-white">
            Choose Your <span className="text-cyan-400">Perfect Model</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Six distinct models designed to meet diverse space requirements and lifestyle needs
          </p>
        </div>

        {/* Product Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12">
          {models.map((model, index) => (
            <div 
              key={index}
              onClick={() => openModal(model)}
              className="group relative bg-black/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl overflow-hidden hover:border-cyan-500/50 hover:shadow-[0_0_30px_rgba(0,217,255,0.2)] transition-all duration-500 cursor-pointer"
            >
              {/* Image */}
              <div className="relative w-full h-64 overflow-hidden">
                <img 
                  src={model.image}
                  alt={model.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute top-4 left-4 px-4 py-2 bg-cyan-500 text-black text-sm font-medium rounded-full">
                  {model.name}
                </div>
                <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">
                  <span className="text-cyan-400 text-sm font-semibold">{model.price}</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-500/10 border border-cyan-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-ruler-line text-lg text-cyan-400"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Dimensions</p>
                      <p className="text-sm font-medium text-white">{model.dimensions}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-500/10 border border-cyan-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-layout-grid-line text-lg text-cyan-400"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Floor Area</p>
                      <p className="text-sm font-medium text-white">{model.area}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-500/10 border border-cyan-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-group-line text-lg text-cyan-400"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Occupancy</p>
                      <p className="text-sm font-medium text-white">{model.occupancy}</p>
                    </div>
                  </div>
                </div>

                {/* Key Feature */}
                <div className="mb-6 p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
                  <p className="text-sm text-gray-300 leading-relaxed">{model.keyFeature}</p>
                </div>

                {/* Features */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {model.features.map((feature, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs rounded-full"
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                {/* Click to View Details */}
                <div className="text-center py-3 bg-cyan-500/10 rounded-lg border border-cyan-500/20 group-hover:border-cyan-500/40 group-hover:bg-cyan-500/20 transition-all">
                  <span className="text-cyan-400 text-sm font-medium">Click to View Details</span>
                  <i className="ri-arrow-right-line text-cyan-400 ml-2"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Custom Configuration */}
        <div className="mb-12 bg-black/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-cyan-500/10 border border-cyan-500/30 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-settings-4-line text-2xl text-cyan-400"></i>
            </div>
            <div>
              <h3 className="text-xl font-medium text-white mb-1">Custom Configurations Available</h3>
              <p className="text-gray-400">Need specific dimensions or features? We offer tailored solutions.</p>
            </div>
          </div>
          <Link
            to="/contact"
            className="px-8 py-3 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-sm font-medium rounded-full hover:bg-cyan-500 hover:text-black transition-all hover:shadow-[0_0_20px_rgba(0,217,255,0.5)] whitespace-nowrap cursor-pointer"
          >
            Discuss Customisation
          </Link>
        </div>

        {/* Comparison Table */}
        <div className="bg-black/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl overflow-hidden shadow-[0_0_30px_rgba(0,217,255,0.1)]">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-cyan-500">
                  <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wide text-black">Model</th>
                  <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wide text-black">Dimensions</th>
                  <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wide text-black">Area</th>
                  <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wide text-black">Occupancy</th>
                  <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wide text-black">Key Feature</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cyan-500/20">
                {models.map((model, index) => (
                  <tr key={index} className="hover:bg-cyan-500/5 transition-colors">
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500 text-black text-sm font-medium rounded-full">
                        {model.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">{model.dimensions}</td>
                    <td className="px-6 py-4 text-sm text-gray-300">{model.area}</td>
                    <td className="px-6 py-4 text-sm text-gray-300">{model.occupancy}</td>
                    <td className="px-6 py-4 text-sm text-gray-300">{model.keyFeature}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden p-4 space-y-4">
            <div className="bg-cyan-500 px-4 py-3 rounded-xl mb-4">
              <h3 className="text-black font-medium text-sm uppercase tracking-wide text-center">Model Comparison</h3>
            </div>
            {models.map((model, index) => (
              <div 
                key={index} 
                className="bg-black/30 border border-cyan-500/20 rounded-xl p-4 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center px-3 py-1 bg-cyan-500 text-black text-sm font-medium rounded-full">
                    {model.name}
                  </span>
                  <span className="text-cyan-400 text-sm font-semibold">{model.price}</span>
                </div>
                
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="bg-cyan-500/5 border border-cyan-500/10 rounded-lg p-3">
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Dimensions</div>
                    <div className="text-sm text-white font-medium">{model.dimensions}</div>
                  </div>
                  <div className="bg-cyan-500/5 border border-cyan-500/10 rounded-lg p-3">
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Area</div>
                    <div className="text-sm text-white font-medium">{model.area}</div>
                  </div>
                  <div className="bg-cyan-500/5 border border-cyan-500/10 rounded-lg p-3 col-span-2">
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Occupancy</div>
                    <div className="text-sm text-white font-medium">{model.occupancy}</div>
                  </div>
                </div>

                <div className="pt-2 border-t border-cyan-500/10">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Key Feature</div>
                  <p className="text-sm text-gray-300 leading-relaxed">{model.keyFeature}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal */}
      {selectedProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
          onClick={closeModal}
        >
          <div
            className="bg-[#0D1117]/95 border border-cyan-500/30 rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-[#0D1117]/95 backdrop-blur-xl border-b border-cyan-500/20 p-6 flex items-center justify-between z-10">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-4 py-1 bg-cyan-500 text-black text-sm font-medium rounded-full">{selectedProduct.name}</span>
                  <span className="text-cyan-400 font-semibold">{selectedProduct.price}</span>
                </div>
                <p className="text-gray-400 text-sm">{selectedProduct.keyFeature}</p>
              </div>
              <button
                onClick={closeModal}
                className="w-10 h-10 flex items-center justify-center bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded-full transition-all cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-white"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 sm:p-8 space-y-8">
              {/* Image Carousel - New Component */}
              <ImageCarousel 
                images={getCarouselImages(selectedProduct)} 
                className="h-96"
              />

              {/* Material List */}
              <div className="bg-black/40 border border-cyan-500/20 rounded-2xl p-6 sm:p-8">
                <h4 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                  <i className="ri-file-list-3-line text-cyan-400"></i>
                  Technical Specifications - Material List
                </h4>
                <div className="space-y-5">
                  {/* Main Frame */}
                  <div className="p-4 bg-cyan-500/5 border border-cyan-500/10 rounded-xl">
                    <div className="text-cyan-400 font-semibold text-sm mb-2">Main Frame:</div>
                    <div className="text-gray-400 text-sm">{selectedProduct.materialList.mainFrame}</div>
                  </div>

                  {/* Exterior Wall */}
                  <div className="p-4 bg-cyan-500/5 border border-cyan-500/10 rounded-xl">
                    <div className="text-cyan-400 font-semibold text-sm mb-2">Exterior Wall:</div>
                    <div className="space-y-1">
                      {selectedProduct.materialList.exteriorWall.map((item, index) => (
                        <div key={index} className="text-gray-400 text-sm">{item}</div>
                      ))}
                    </div>
                  </div>

                  {/* Glass */}
                  <div className="p-4 bg-cyan-500/5 border border-cyan-500/10 rounded-xl">
                    <div className="text-cyan-400 font-semibold text-sm mb-2">Glass:</div>
                    <div className="space-y-1">
                      {selectedProduct.materialList.glass.map((item, index) => (
                        <div key={index} className="text-gray-400 text-sm">{item}</div>
                      ))}
                    </div>
                  </div>

                  {/* Wall */}
                  <div className="p-4 bg-cyan-500/5 border border-cyan-500/10 rounded-xl">
                    <div className="text-cyan-400 font-semibold text-sm mb-2">Wall:</div>
                    <div className="space-y-1">
                      {selectedProduct.materialList.wall.map((item, index) => (
                        <div key={index} className="text-gray-400 text-sm">{item}</div>
                      ))}
                    </div>
                  </div>

                  {/* Floor */}
                  <div className="p-4 bg-cyan-500/5 border border-cyan-500/10 rounded-xl">
                    <div className="text-cyan-400 font-semibold text-sm mb-2">Floor:</div>
                    <div className="text-gray-400 text-sm">{selectedProduct.materialList.floor}</div>
                  </div>

                  {/* Insulation */}
                  <div className="p-4 bg-cyan-500/5 border border-cyan-500/10 rounded-xl">
                    <div className="text-cyan-400 font-semibold text-sm mb-2">Insulation:</div>
                    <div className="text-gray-400 text-sm">{selectedProduct.materialList.insulation}</div>
                  </div>
                </div>
              </div>

              {/* Selling Points */}
              <div className="bg-gradient-to-r from-cyan-500/10 to-cyan-600/10 border border-cyan-500/30 rounded-2xl p-6">
                <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                  <i className="ri-lightbulb-flash-line text-cyan-400"></i>
                  Why Choose {selectedProduct.name}
                </h4>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {selectedProduct.sellingPoints}
                </p>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-black/40 border border-cyan-500/20 rounded-xl p-4 text-center">
                  <i className="ri-ruler-line text-2xl text-cyan-400 mb-2"></i>
                  <div className="text-xs text-gray-400 mb-1">Dimensions</div>
                  <div className="text-sm font-semibold text-white break-words">{selectedProduct.dimensions}</div>
                </div>
                <div className="bg-black/40 border border-cyan-500/20 rounded-xl p-4 text-center">
                  <i className="ri-layout-grid-line text-2xl text-cyan-400 mb-2"></i>
                  <div className="text-xs text-gray-400 mb-1">Floor Area</div>
                  <div className="text-sm font-semibold text-white">{selectedProduct.area}</div>
                </div>
                <div className="bg-black/40 border border-cyan-500/20 rounded-xl p-4 text-center">
                  <i className="ri-group-line text-2xl text-cyan-400 mb-2"></i>
                  <div className="text-xs text-gray-400 mb-1">Occupancy</div>
                  <div className="text-sm font-semibold text-white break-words">{selectedProduct.occupancy}</div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link
                  to={`/contact?product=${encodeURIComponent(selectedProduct.name)}`}
                  className="flex-1 px-8 py-4 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all hover:shadow-[0_0_30px_rgba(0,217,255,0.6)] whitespace-nowrap cursor-pointer text-center"
                >
                  Request Quote
                </Link>
                <button
                  onClick={closeModal}
                  className="flex-1 px-8 py-4 bg-cyan-500/10 border border-cyan-500/30 text-white text-base font-medium rounded-full hover:bg-cyan-500/20 hover:border-cyan-500/50 transition-all whitespace-nowrap cursor-pointer"
                >
                  Continue Browsing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
